import type {
  ArtifactSourceError,
  ArtifactSourceLoading,
  ArtifactSourceReady,
  ArtifactSourceRef,
  ArtifactSourceState,
} from "@entrilia/shared/artifactRuntime/protocol";
import type { StateAction } from "@entrilia/shared/types";
import type { SmartViewArtifactDrillDown } from "../../../../../api/types/smartViewArtifactsTypes";

const MAX_RUNS_IN_FLIGHT = 4;

export interface SourceRun {
  runId: number;
  identity: string;
  sourceKey: string;
  filterValues: Record<string, string[]>;
  overrides: Record<string, string[]>;
  drillDown: boolean;
}

export interface SourceTarget {
  sourceKey: string;
  filterValues: Record<string, string[]>;
  overrides: Record<string, string[]>;
}

export interface SourceFigureDrillDown {
  sessionId: string;
  etag: string;
  drillDownId: string;
  reversedSign: boolean;
}

export interface SourceFigureTarget extends SourceTarget {
  columnKey: string;
  row?: number | "totals";
  value?: unknown;
  drillDown?: SourceFigureDrillDown;
}

export type SourceNoteKind = "failed" | "empty";

export interface SourceNote {
  sourceKey: string;
  kind: SourceNoteKind;
  message?: string;
}

export interface SourceStateUpdate {
  clientId: string;
  subscriptionId: string;
  state: ArtifactSourceState;
}

interface SourceEntry {
  run: SourceRun;
  inFlight: boolean;
  state: ArtifactSourceState;
  drillDown?: SmartViewArtifactDrillDown;
}

interface SourceSubscription {
  clientId: string;
  subscriptionId: string;
  sourceKey: string;
  overrides: Record<string, string[]>;
  identity: string;
  drillDown: boolean;
}

export interface SourceRequestsState {
  version: number;
  boundFilterKeys: ReadonlyMap<string, string[]>;
  filterValues: Record<string, string[]>;
  subscriptions: Record<string, SourceSubscription>;
  entries: Record<string, SourceEntry>;
  lastRunId: number;
  drillClientIds: ReadonlySet<string>;
}

const loadingState: ArtifactSourceLoading = { status: "loading" };

const toSubscriptionKey = (clientId: string, subscriptionId: string): string =>
  JSON.stringify([clientId, subscriptionId]);

export const createSourceRequestsState = (
  version: number,
  sources: ArtifactSourceRef[],
  filterValues: Record<string, string[]>
): SourceRequestsState => ({
  version,
  boundFilterKeys: new Map(sources.map((source) => [source.key, source.boundFilterKeys])),
  filterValues,
  subscriptions: {},
  entries: {},
  lastRunId: 0,
  drillClientIds: new Set(),
});

export const getUnknownSourceError = (
  state: SourceRequestsState,
  sourceKey: string
): ArtifactSourceError | undefined =>
  state.boundFilterKeys.has(sourceKey)
    ? undefined
    : { status: "error", message: `Source "${sourceKey}" does not exist.` };

const getFilterValues = (state: SourceRequestsState, key: string): string[] =>
  (Object.hasOwn(state.filterValues, key) ? state.filterValues[key] : undefined) ?? [];

const getIdentity = (
  state: SourceRequestsState,
  sourceKey: string,
  overrides: Record<string, string[]>,
  drillDown: boolean
): string => {
  const boundValues = (state.boundFilterKeys.get(sourceKey) ?? []).map((key) => getFilterValues(state, key));
  const sortedOverrides = Object.entries(overrides).sort(([left], [right]) => left.localeCompare(right));
  return JSON.stringify([state.version, sourceKey, sortedOverrides, boundValues, drillDown]);
};

const ensureEntry = (
  state: SourceRequestsState,
  identity: string,
  sourceKey: string,
  overrides: Record<string, string[]>,
  drillDown: boolean
): SourceRequestsState => {
  if (state.entries[identity]) {
    return state;
  }
  const runId = state.lastRunId + 1;
  const run: SourceRun = { runId, identity, sourceKey, filterValues: state.filterValues, overrides, drillDown };
  return {
    ...state,
    lastRunId: runId,
    entries: { ...state.entries, [identity]: { run, inFlight: false, state: loadingState } },
  };
};

const getSubscribedIdentities = (state: SourceRequestsState): Set<string> =>
  new Set(Object.values(state.subscriptions).map((subscription) => subscription.identity));

const startQueuedRuns = (state: SourceRequestsState): SourceRequestsState => {
  const entries = Object.values(state.entries);
  const capacity = MAX_RUNS_IN_FLIGHT - entries.filter((entry) => entry.inFlight).length;
  const subscribedIdentities = getSubscribedIdentities(state);
  const queued = entries
    .filter(
      (entry) => !entry.inFlight && entry.state.status === "loading" && subscribedIdentities.has(entry.run.identity)
    )
    .sort((left, right) => left.run.runId - right.run.runId)
    .slice(0, Math.max(capacity, 0));
  if (queued.length === 0) {
    return state;
  }
  const startedEntries = queued.map((entry): [string, SourceEntry] => [
    entry.run.identity,
    { ...entry, inFlight: true, run: { ...entry.run, filterValues: state.filterValues } },
  ]);
  return { ...state, entries: { ...state.entries, ...Object.fromEntries(startedEntries) } };
};

// Leaves state "loading" (not removed) so getRunsToAbort can tell a freed run from a settled one.
const freeOrphanedRuns = (state: SourceRequestsState): SourceRequestsState => {
  const subscribedIdentities = getSubscribedIdentities(state);
  const orphaned = Object.values(state.entries).filter(
    (entry) => entry.inFlight && !subscribedIdentities.has(entry.run.identity)
  );
  if (orphaned.length === 0) {
    return state;
  }
  const freedEntries = orphaned.map((entry): [string, SourceEntry] => [
    entry.run.identity,
    { ...entry, inFlight: false },
  ]);
  return { ...state, entries: { ...state.entries, ...Object.fromEntries(freedEntries) } };
};

export const subscribeAction =
  (
    clientId: string,
    subscriptionId: string,
    sourceKey: string,
    overrides: Record<string, string[]>
  ): StateAction<SourceRequestsState> =>
  (state) => {
    if (getUnknownSourceError(state, sourceKey)) {
      return state;
    }
    const drillDown = state.drillClientIds.has(clientId);
    const identity = getIdentity(state, sourceKey, overrides, drillDown);
    const withEntry = ensureEntry(state, identity, sourceKey, overrides, drillDown);
    const subscription: SourceSubscription = { clientId, subscriptionId, sourceKey, overrides, identity, drillDown };
    return startQueuedRuns({
      ...withEntry,
      subscriptions: { ...withEntry.subscriptions, [toSubscriptionKey(clientId, subscriptionId)]: subscription },
    });
  };

export const unsubscribeAction =
  (clientId: string, subscriptionId: string): StateAction<SourceRequestsState> =>
  (state) => {
    const { [toSubscriptionKey(clientId, subscriptionId)]: _removed, ...subscriptions } = state.subscriptions;
    return startQueuedRuns(freeOrphanedRuns({ ...state, subscriptions }));
  };

export const removeClientAction =
  (clientId: string): StateAction<SourceRequestsState> =>
  (state) => {
    const subscriptions = Object.entries(state.subscriptions).filter(
      ([, subscription]) => subscription.clientId !== clientId
    );
    return startQueuedRuns(freeOrphanedRuns({ ...state, subscriptions: Object.fromEntries(subscriptions) }));
  };

export const changeFilterValuesAction =
  (filterValues: Record<string, string[]>): StateAction<SourceRequestsState> =>
  (state) => {
    let next: SourceRequestsState = { ...state, filterValues };
    const subscriptions: Record<string, SourceSubscription> = {};
    for (const [subscriptionKey, subscription] of Object.entries(state.subscriptions)) {
      const identity = getIdentity(next, subscription.sourceKey, subscription.overrides, subscription.drillDown);
      next = ensureEntry(next, identity, subscription.sourceKey, subscription.overrides, subscription.drillDown);
      subscriptions[subscriptionKey] =
        identity === subscription.identity ? subscription : { ...subscription, identity };
    }
    return startQueuedRuns(freeOrphanedRuns({ ...next, subscriptions }));
  };

export const changeDrillModeAction =
  (clientId: string, drillDown: boolean): StateAction<SourceRequestsState> =>
  (state) => {
    if (state.drillClientIds.has(clientId) === drillDown) {
      return state;
    }
    const drillClientIds = new Set(state.drillClientIds);
    if (drillDown) {
      drillClientIds.add(clientId);
    } else {
      drillClientIds.delete(clientId);
    }
    const subscribedIdentities = getSubscribedIdentities(state);
    const entries = drillDown
      ? Object.fromEntries(
          Object.entries(state.entries).filter(
            ([identity, entry]) =>
              !entry.run.drillDown || entry.state.status === "loading" || subscribedIdentities.has(identity)
          )
        )
      : state.entries;
    let next: SourceRequestsState = { ...state, drillClientIds, entries };
    const subscriptions: Record<string, SourceSubscription> = {};
    for (const [subscriptionKey, subscription] of Object.entries(state.subscriptions)) {
      if (subscription.clientId !== clientId) {
        subscriptions[subscriptionKey] = subscription;
        continue;
      }
      const identity = getIdentity(next, subscription.sourceKey, subscription.overrides, drillDown);
      next = ensureEntry(next, identity, subscription.sourceKey, subscription.overrides, drillDown);
      subscriptions[subscriptionKey] = { ...subscription, identity, drillDown };
    }
    return startQueuedRuns(freeOrphanedRuns({ ...next, subscriptions }));
  };

export const settleRunAction =
  (
    run: SourceRun,
    result: ArtifactSourceReady | ArtifactSourceError,
    drillDown?: SmartViewArtifactDrillDown
  ): StateAction<SourceRequestsState> =>
  (state) => {
    const entry = state.entries[run.identity];
    if (!entry?.inFlight || entry.run.runId !== run.runId) {
      return state;
    }
    return startQueuedRuns({
      ...state,
      entries: { ...state.entries, [run.identity]: { ...entry, inFlight: false, state: result, drillDown } },
    });
  };

export const refreshAction = (): StateAction<SourceRequestsState> => (state) => {
  let next: SourceRequestsState = { ...state, entries: {} };
  for (const subscription of Object.values(state.subscriptions)) {
    next = ensureEntry(
      next,
      subscription.identity,
      subscription.sourceKey,
      subscription.overrides,
      subscription.drillDown
    );
  }
  return startQueuedRuns(next);
};

const readOwn = <T>(record: Record<string, T> | undefined, key: string): T | undefined =>
  record && Object.hasOwn(record, key) ? record[key] : undefined;

export const getFigureTarget = (
  state: SourceRequestsState,
  clientId: string,
  subscriptionId: string,
  row: number | "totals" | undefined,
  columnKey: string
): SourceFigureTarget | undefined => {
  const subscription = readOwn(state.subscriptions, toSubscriptionKey(clientId, subscriptionId));
  if (!subscription?.drillDown) {
    return undefined;
  }
  const entry = readOwn(state.entries, subscription.identity);
  if (entry?.state.status !== "ready" || !entry.state.columns.some((column) => column.key === columnKey)) {
    return undefined;
  }
  const isMeasure = entry.state.columns.some((column) => column.key === columnKey && column.isMeasure);
  const target: SourceFigureTarget = {
    sourceKey: subscription.sourceKey,
    filterValues: entry.run.filterValues,
    overrides: subscription.overrides,
    columnKey,
  };
  if (row === undefined) {
    return isMeasure ? target : undefined;
  }
  const values = row === "totals" ? entry.state.totals : entry.state.rows[row];
  if (!values) {
    return undefined;
  }
  const drillDownIds = row === "totals" ? entry.drillDown?.totals : entry.drillDown?.rows[row];
  const drillDownId = readOwn(drillDownIds, columnKey);
  if (!isMeasure && !drillDownId) {
    return undefined;
  }
  const drillDown = entry.drillDown &&
    drillDownId && {
      sessionId: entry.drillDown.sessionId,
      etag: entry.drillDown.etag,
      drillDownId,
      reversedSign: entry.drillDown.reversedSignColumns.includes(columnKey),
    };
  return { ...target, row, value: readOwn(values, columnKey), ...(drillDown && { drillDown }) };
};

export const listActiveTargets = (state: SourceRequestsState, clientId: string): SourceTarget[] => {
  const targets = new Map<string, SourceTarget>();
  for (const subscription of Object.values(state.subscriptions)) {
    if (subscription.clientId === clientId && !targets.has(subscription.identity)) {
      targets.set(subscription.identity, {
        sourceKey: subscription.sourceKey,
        filterValues: state.filterValues,
        overrides: subscription.overrides,
      });
    }
  }
  return [...targets.values()];
};

export const toSourceNote = (sourceKey: string, state: ArtifactSourceState | undefined): SourceNote | undefined => {
  if (state?.status === "error") {
    return { sourceKey, kind: "failed", message: state.message };
  }
  return state?.status === "ready" && state.rowCount === 0 ? { sourceKey, kind: "empty" } : undefined;
};

export const listSourceNotes = (state: SourceRequestsState, clientId: string): SourceNote[] => {
  const notes: SourceNote[] = [];
  for (const subscription of Object.values(state.subscriptions)) {
    const note =
      subscription.clientId === clientId
        ? toSourceNote(subscription.sourceKey, state.entries[subscription.identity]?.state)
        : undefined;
    if (note && !notes.some(({ sourceKey, kind }) => sourceKey === note.sourceKey && kind === note.kind)) {
      notes.push(note);
    }
  }
  return notes;
};

export const getRunsToStart = (previous: SourceRequestsState, next: SourceRequestsState): SourceRun[] => {
  const runningBefore = new Set(
    Object.values(previous.entries)
      .filter((entry) => entry.inFlight)
      .map((entry) => entry.run.runId)
  );
  return Object.values(next.entries)
    .filter((entry) => entry.inFlight && !runningBefore.has(entry.run.runId))
    .map((entry) => entry.run);
};

export const getRunsToAbort = (previous: SourceRequestsState, next: SourceRequestsState): SourceRun[] =>
  Object.values(previous.entries)
    .filter((entry) => entry.inFlight)
    .flatMap((entry) => {
      const nextEntry = next.entries[entry.run.identity];
      const wasFreed = nextEntry && !nextEntry.inFlight && nextEntry.state.status === "loading";
      return wasFreed ? [entry.run] : [];
    });

const getSubscriptionState = (state: SourceRequestsState, subscription: SourceSubscription | undefined) =>
  (subscription && state.entries[subscription.identity]?.state) ?? loadingState;

export const getSourceStateUpdates = (previous: SourceRequestsState, next: SourceRequestsState): SourceStateUpdate[] =>
  Object.entries(next.subscriptions).flatMap(([subscriptionKey, subscription]) => {
    const state = getSubscriptionState(next, subscription);
    const before = getSubscriptionState(previous, previous.subscriptions[subscriptionKey]);
    const { clientId, subscriptionId } = subscription;
    return state === before ? [] : [{ clientId, subscriptionId, state }];
  });
